/**
 * Package Copy Service
 *
 * Creates independent structural copies of CustomSkillPackages.
 * Used by both private sharing (accept invitation) and community publishing (add package).
 *
 * Key principles:
 * - Copies contain categories and skills only — ZERO ratings
 * - The copy is owned by the recipient — completely independent of the original
 * - After copying, changes to the original do NOT affect the copy
 */

import { generateClient } from 'aws-amplify/data';
import type { Schema } from "@backend-schema";
import type { CopyPackageParams, CopyPackageResult } from '../types/packageSharing';

const client = generateClient<Schema>();

/**
 * Create an independent structural copy of a CustomSkillPackage.
 * Copies all categories and skills but NO ratings.
 * The copy is owned by the current authenticated user.
 */
export async function copyPackage(params: CopyPackageParams): Promise<CopyPackageResult> {
  const { sourcePackageId, sourceVersion, sharedByName } = params;

  // 1. Fetch the source package
  const sourceResult = await client.models.CustomSkillPackages.get({ id: sourcePackageId });
  if (!sourceResult.data) {
    throw new Error('Source package not found');
  }

  const sourcePackage = sourceResult.data;
  let newPackageId = '';
  const createdCategoryIds: string[] = [];
  const createdSkillIds: string[] = [];

  try {
    // 2. Create the new package (owned by current user)
    const newPackageResult = await client.models.CustomSkillPackages.create({
      name: sourcePackage.name || 'Unnamed Package',
      description: sourcePackage.description || undefined,
      visibility: 'shared',
      version: sourceVersion,
      sourcePackageId,
      sourceVersion,
      sharedByName,
    });

    if (!newPackageResult.data?.id) {
      throw new Error('Failed to create package copy');
    }

    newPackageId = newPackageResult.data.id;

    // 3. Fetch all categories for the source package
    const categoriesResult = await client.models.CustomSkillCategories.list({
      filter: { customPackageId: { eq: sourcePackageId } },
      limit: 100,
    });

    // 4. Copy each category and its skills
    for (const category of categoriesResult.data) {
      const newCategoryResult = await client.models.CustomSkillCategories.create({
        name: category.name || 'Unnamed Category',
        customPackageId: newPackageId,
      });

      if (!newCategoryResult.data?.id) continue;
      createdCategoryIds.push(newCategoryResult.data.id);

      // Fetch skills for this category
      const skillsResult = await client.models.CustomSkills.list({
        filter: { customCategoryId: { eq: category.id ?? undefined } },
        limit: 200,
      });

      // Copy each skill (NO ratings)
      for (const skill of skillsResult.data) {
        const newSkillResult = await client.models.CustomSkills.create({
          name: skill.name || 'Unnamed Skill',
          customCategoryId: newCategoryResult.data.id,
        });

        if (newSkillResult.data?.id) {
          createdSkillIds.push(newSkillResult.data.id);
        }
      }
    }

    return {
      newPackageId,
      categoriesCopied: createdCategoryIds.length,
      skillsCopied: createdSkillIds.length,
    };
  } catch (error) {
    // Cleanup on failure — delete any partially created records
    console.error('Package copy failed, cleaning up:', error);

    for (const skillId of createdSkillIds) {
      try { await client.models.CustomSkills.delete({ id: skillId }); } catch { /* ignore */ }
    }
    for (const categoryId of createdCategoryIds) {
      try { await client.models.CustomSkillCategories.delete({ id: categoryId }); } catch { /* ignore */ }
    }
    if (newPackageId) {
      try { await client.models.CustomSkillPackages.delete({ id: newPackageId }); } catch { /* ignore */ }
    }

    throw error;
  }
}

/**
 * Check if a user already has a copy of a specific source package.
 * Used to prevent duplicate copies.
 */
export async function hasExistingCopy(sourcePackageId: string): Promise<boolean> {
  const result = await client.models.CustomSkillPackages.list({
    filter: { sourcePackageId: { eq: sourcePackageId } },
    limit: 1,
  });

  return result.data.length > 0;
}
